import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { useState } from 'react';
import { useCharger } from '../../api/chargers';
import { usePermission } from '../../auth/permissions';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { ConfigurationTab } from './ConfigurationTab';
import { ConnectorStatuses } from './ConnectorStatuses';
import { HistoryTab } from './HistoryTab';
import { MessagesTab } from './MessagesTab';
import { OverviewTab } from './OverviewTab';
import { PricingTab } from './PricingTab';
import { SecurityTab } from './SecurityTab';
import { SettingsTab } from './SettingsTab';
import { colours } from '../../theme';
import { useRemoveChargerFlow } from './useRemoveCharger';
import { chargerName } from './status';
import { useLeaveGuard } from '../../components/unsaved/unsaved';

type TabId = 'overview' | 'settings' | 'pricing' | 'security' | 'configuration' | 'history' | 'messages';

/**
 * Everything about one charger, live: status and sessions with remote control, its
 * settings, security, the charger's own configuration, command history and the raw OCPP
 * messages. Managers see it read only: status, prices, the configuration and history, with
 * read-only commands (ask for its status, read the configuration).
 */
export function ChargerDialog({ chargerId, initialTab = 'overview', close }: { chargerId: string; initialTab?: TabId } & DialogProps) {
  const { data: c, isPending, error } = useCharger(chargerId);
  const [tab, setTab] = useState<TabId>(initialTab);
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const canConfigure = usePermission('chargers.configure');
  const canEdit = usePermission('chargers.edit');
  const canQuery = usePermission('chargers.query');
  const canRegister = usePermission('chargers.register');
  const removeCharger = useRemoveChargerFlow();
  // Closing or switching tabs asks first about unsaved settings, prices or security.
  const guard = useLeaveGuard();
  const canSeeLog = usePermission('ocpp.logs.view');

  const tabs: { id: TabId; label: string; show: boolean }[] = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'settings', label: 'Settings', show: canEdit },
    { id: 'pricing', label: 'Pricing', show: true },
    { id: 'security', label: 'Security', show: canConfigure },
    { id: 'configuration', label: 'Configuration', show: canQuery },
    { id: 'history', label: 'History', show: true },
    { id: 'messages', label: 'Messages', show: canSeeLog },
  ];

  return (
    <Dialog open onClose={() => guard.close(close)} fullWidth maxWidth="md" fullScreen={phone} aria-labelledby="charger-title">
      <DialogTitle id="charger-title" component="div">
        {c ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ fontSize: 22, fontWeight: 500 }}>{chargerName(c)}</Typography>
              <Typography color="textSecondary">
                {(c.organisationSiteCount > 1 ? [c.siteName, c.bay ? `Bay ${c.bay}` : null, c.organisationName] : [c.organisationName, c.bay ? `Bay ${c.bay}` : null]).filter(Boolean).join(' · ') ||
                  'Not on a site yet'}
              </Typography>
            </Box>
            <Box sx={{ pt: 0.5, fontSize: 16, lineHeight: '24px' }}>
              <ConnectorStatuses charger={c} />
            </Box>
          </Box>
        ) : (
          <Skeleton width={240} />
        )}
      </DialogTitle>
      <Box sx={{ px: 3 }}>
        <Tabs value={tab} onChange={(_e, t: TabId) => guard.switchTab(() => setTab(t))} variant="scrollable" allowScrollButtonsMobile aria-label="Charger sections">
          {tabs.filter((t) => t.show).map((t) => <Tab key={t.id} value={t.id} label={t.label} />)}
        </Tabs>
      </Box>
      <DialogContent sx={{ minHeight: { sm: 480 }, pt: '24px !important' }}>
        {error ? (
          <Typography color="error">{error.message}</Typography>
        ) : isPending || !c ? (
          <Skeleton variant="rounded" height={200} />
        ) : (
          <>
            {tab === 'overview' && <OverviewTab charger={c} />}
            {tab === 'settings' && <SettingsTab charger={c} />}
            {tab === 'pricing' && <PricingTab charger={c} />}
            {tab === 'security' && <SecurityTab charger={c} />}
            {tab === 'configuration' && <ConfigurationTab charger={c} />}
            {tab === 'history' && <HistoryTab charger={c} />}
            {tab === 'messages' && <MessagesTab charger={c} />}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {c && canRegister && (
          <Button
            variant="text"
            onClick={async () => {
              if (await removeCharger(c)) close();
            }}
            sx={{ mr: 'auto', color: colours.faultRed }}
          >
            Remove charger
          </Button>
        )}
        <Button variant="contained" onClick={() => guard.close(close)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
